/**
 * Technology Capability Create Route
 *
 * Create a new technology capability using TechnologyCapabilityForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { TechnologyCapabilityForm } from '../../components/technology-capabilities';
import {
  createTechnologyCapability,
  type CreateTechnologyCapabilityInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateTechnologyCapabilityFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/technology-capabilities/new')({
  component: TechnologyCapabilityCreatePage,
});

function TechnologyCapabilityCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateTechnologyCapabilityFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateTechnologyCapabilityInput = {
        name: formData.name,
        description: formData.description || undefined,
        domainId: formData.domainId || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      const newTechnologyCapability = await createTechnologyCapability(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListTechnologyCapabilities'],
      });

      // Navigate to the new technology capability detail page
      navigate({
        to: `/technology-capabilities/${newTechnologyCapability.entityId}` as string,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/technology-capabilities', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="technology-capability-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Technology Capability</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new technology capability
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TechnologyCapabilityForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
